import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import {
  Box,
  IconButton,
  InputAdornment,
  Popover,
  TextField,
} from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import "react-datepicker/dist/react-datepicker.css";
import "./DatetimePicker.css";
import { FILTER_FONT_SIZE } from "./filterBarStyles";
import {
  clampScheduledDate,
  dateToIso,
  formatScheduleDisplay,
  getScheduleBounds,
  isoToDate,
  parseScheduleInput,
} from "./scheduleUtils";

/**
 * Editable text field + separate calendar popover (typing is never blocked by react-datepicker).
 */
const DatetimePicker = ({
  value,
  onChange,
  label = "Run at",
  placeholder = "Jun 3, 2026 12:15 PM",
  error = false,
  helperText,
  fullWidth = false,
  disabled = false,
}) => {
  const anchorRef = useRef(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const { minDt, maxDt } = useMemo(() => getScheduleBounds(), []);
  const selected = isoToDate(value);

  const [inputText, setInputText] = useState(() => formatScheduleDisplay(value));

  useEffect(() => {
    setInputText(formatScheduleDisplay(value));
  }, [value]);

  const commitTypedValue = useCallback(() => {
    const text = inputText;
    if (!text.trim()) {
      onChange(null);
      setInputText("");
      return;
    }
    const parsed = parseScheduleInput(text);
    if (!parsed) {
      setInputText(formatScheduleDisplay(value));
      return;
    }
    const clamped = clampScheduledDate(parsed);
    const iso = dateToIso(clamped);
    onChange(iso);
    setInputText(formatScheduleDisplay(iso));
  }, [inputText, onChange, value]);

  const isTimeListClick = (target) =>
    target instanceof HTMLElement &&
    (target.classList.contains("react-datepicker__time-list-item") ||
      Boolean(target.closest?.(".react-datepicker__time-list-item")));

  const handlePickerChange = (date, event) => {
    if (!date) {
      onChange(null);
      setInputText("");
      return;
    }
    const clamped = clampScheduledDate(date);
    const iso = dateToIso(clamped);
    onChange(iso);
    setInputText(formatScheduleDisplay(iso));

    if (isTimeListClick(event?.target)) {
      setCalendarOpen(false);
    }
  };

  const handleCalendarClick = (e) => {
    if (isTimeListClick(e.target)) {
      setTimeout(() => setCalendarOpen(false), 0);
    }
  };

  const filterTime = (time) => {
    const base = selected || minDt;
    const candidate = new Date(base);
    candidate.setHours(time.getHours(), time.getMinutes(), 0, 0);
    return candidate >= minDt && candidate <= maxDt;
  };

  return (
    <Box
      ref={anchorRef}
      className="remediation-datetime-wrapper"
      sx={{
        display: "inline-block",
        width: fullWidth ? "100%" : "auto",
        minWidth: fullWidth ? undefined : 280,
      }}
    >
      <TextField
        size="small"
        label={label}
        placeholder={placeholder}
        fullWidth={fullWidth}
        disabled={disabled}
        error={error}
        helperText={helperText}
        value={inputText}
        onChange={(e) => setInputText(e.target.value)}
        onBlur={commitTypedValue}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commitTypedValue();
          }
        }}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                size="small"
                disabled={disabled}
                onClick={() => setCalendarOpen(true)}
                aria-label="Open calendar"
                edge="end"
              >
                <EventIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </InputAdornment>
          ),
          sx: {
            fontSize: FILTER_FONT_SIZE,
            height: 36,
            borderRadius: 1.5,
            backgroundColor: "background.paper",
          },
        }}
        sx={{
          "& .MuiInputBase-input": { fontSize: FILTER_FONT_SIZE },
          "& .MuiFormHelperText-root": { fontSize: 11, mx: 0 },
        }}
      />

      <Popover
        open={calendarOpen && !disabled}
        anchorEl={anchorRef.current}
        onClose={() => setCalendarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            className: "remediation-datetime-popover-paper",
            sx: {
              mt: 0.5,
              overflow: "visible",
              boxShadow: "0 4px 6px -1px rgba(0,0,0,0.08), 0 2px 4px -2px rgba(0,0,0,0.06)",
            },
          },
        }}
        sx={{ zIndex: 1600 }}
      >
        <Box className="remediation-datetime-popper" onClick={handleCalendarClick}>
          <DatePicker
            inline
            selected={selected}
            onChange={handlePickerChange}
            showTimeSelect
            timeIntervals={1}
            timeCaption="Time"
            minDate={minDt}
            maxDate={maxDt}
            filterTime={filterTime}
          />
        </Box>
      </Popover>
    </Box>
  );
};

export default DatetimePicker;
